/**
 * Vocabulary of `main.organization_ai_api_key` (#2731): the AI providers an
 * organization can store a key for, and who in the organization may use it.
 * The values match the CHECK constraints in schema/sql/09-ai-assistant.sql.
 */
export const AI_PROVIDERS = ['anthropic'] as const
export type AiProvider = (typeof AI_PROVIDERS)[number]

export const AI_API_KEY_ACCESS_MODES = ['off', 'organization', 'selected'] as const
export type AiApiKeyAccessMode = (typeof AI_API_KEY_ACCESS_MODES)[number]

export const isAiProvider = (value: string): value is AiProvider => AI_PROVIDERS.includes(value as AiProvider)

export const isAiApiKeyAccessMode = (value: string): value is AiApiKeyAccessMode =>
  AI_API_KEY_ACCESS_MODES.includes(value as AiApiKeyAccessMode)

/**
 * Whether a user may use the AI assistant in an organization, derived from the
 * organization's keys and their access modes. `/me/permissions` reports it for
 * the chosen organization; `providers` lists the providers that grant access.
 */
export type AssistantAccess = {
  is_available: boolean
  providers: AiProvider[]
}

/** The access a user has with no organization chosen, or no key granting it. */
export const noAssistantAccess = (): AssistantAccess => ({ is_available: false, providers: [] })

/** Number of trailing key characters stored in plain text as `api_key_hint`. */
export const AI_API_KEY_HINT_LENGTH = 4

/** The hint shown for a key: the masked prefix followed by its last characters. */
export const aiApiKeyHint = (apiKey: string): string => apiKey.slice(-AI_API_KEY_HINT_LENGTH)

/**
 * Error code the mainapp assistant endpoints answer 403 with when the user's
 * organization has no enabled key that grants the user access. Turing's own
 * key is never a fallback, so the frontend hides the assistant on this code.
 */
export const ASSISTANT_NO_ORGANIZATION_KEY_CODE = 'ASSISTANT_NO_ORGANIZATION_KEY'
